import { useState, useCallback } from 'react';
import { Message } from '../types';
import { processAudio, processText } from '../api/client';
import { useAudioRecorder } from './useAudioRecorder';
import { WebSpeechSttAdapter } from '../adapters/stt/web-speech.adapter';
import { WebSpeechTtsAdapter } from '../adapters/tts/web-speech.adapter';

type ConversationState = 'idle' | 'recording' | 'processing' | 'playing' | 'error';

const webSpeechStt = new WebSpeechSttAdapter();
const webSpeechTts = new WebSpeechTtsAdapter();

export function useConversation(sttProvider: string, ttsProvider: string, language: string) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [state, setState] = useState<ConversationState>('idle');
  const [error, setError] = useState<string | null>(null);
  const { isRecording, startRecording, stopRecording } = useAudioRecorder();

  const addMessage = useCallback((msg: Omit<Message, 'id' | 'timestamp'>) => {
    const message: Message = {
      ...msg,
      id: crypto.randomUUID(),
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, message]);
    return message;
  }, []);

  const startVoiceInput = useCallback(async () => {
    setError(null);
    try {
      if (sttProvider === 'webSpeech') {
        setState('recording');
        webSpeechStt.startListening();
      } else {
        setState('recording');
        await startRecording();
      }
    } catch (e: any) {
      setError(e.message);
      setState('error');
    }
  }, [sttProvider, startRecording]);

  const stopVoiceInput = useCallback(async () => {
    setState('processing');
    try {
      let result;

      if (sttProvider === 'webSpeech') {
        const sttResult = await webSpeechStt.stopListening();
        const userText = sttResult.text;

        addMessage({ role: 'user', text: userText });

        result = await processText({ text: userText, sttProvider, ttsProvider, language });
      } else {
        const { blob } = await stopRecording();
        result = await processAudio({ audio: blob, sttProvider, ttsProvider, language });

        addMessage({ role: 'user', text: result.transcribedText });
      }

      addMessage({
        role: 'assistant',
        text: result.llmResponseText,
        metrics: result.metrics,
      });

      // Play audio response
      setState('playing');
      if (result.audio) {
        const url = URL.createObjectURL(result.audio);
        const audio = new Audio(url);
        await new Promise<void>((resolve) => {
          audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
          audio.onerror = () => { URL.revokeObjectURL(url); resolve(); };
          // play() can reject (e.g. autoplay blocked); resolve so we never hang in 'playing'.
          audio.play().catch(() => { URL.revokeObjectURL(url); resolve(); });
        });
      } else if (ttsProvider === 'browserTts') {
        await webSpeechTts.speak(result.llmResponseText);
      }

      setState('idle');
    } catch (e: any) {
      setError(e.message);
      setState('error');
    }
  }, [sttProvider, ttsProvider, language, stopRecording, addMessage]);

  const clearMessages = useCallback(() => setMessages([]), []);

  return {
    messages,
    state,
    error,
    isRecording,
    startVoiceInput,
    stopVoiceInput,
    clearMessages,
  };
}
